import { useEffect, useMemo, useState } from 'react';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { API_BASE_URL, authFetch } from '@/services/api';
import BessSocPowerChart from './components/BessSocPowerChart';

interface Nameplate {
  powerKw: number | null; energyKwh: number | null; manufacturer: string | null;
  model: string | null; chemistry: string | null; units: number | null;
  commissioned: string | null; notes: string | null;
}
interface Live {
  readAtLocal: string; readAtUtc: string | null; runningStatus: string | null; powerFlow: string | null;
  socPct: number | null; sohPct: number | null; outputKw: number | null; dcPowerKw: number | null;
  dcVoltageV: number | null; dcCurrentA: number | null; innerTempC: number | null; radiatorTempC: number | null;
  gridHz: number | null; dailyChargeKwh: number | null; dailyDischargeKwh: number | null;
  totalChargeKwh: number | null; totalDischargeKwh: number | null; availableKva: number | null; faults: number[];
}
interface BessSystem {
  id: string; assetId: string; name: string; region: string; installedMw: number;
  status: 'active' | 'planned' | string; nameplate: Nameplate; online: boolean; live: Live | null;
}

// Dark surface tokens (this page is always dark, like /forecast)
const cardBg = '#1A1A1A';
const cardBorder = 'rgba(255,255,255,0.07)';
const textMain = '#E0E0E0';
const textMuted = '#6B6B70';
const textDim = '#4A4A50';
const innerBg = '#0F0F0F';
const hoverBg = 'rgba(255,255,255,0.05)';

const statusUk = (rs: string | null) => {
  const v = (rs || '').toLowerCase();
  if (v === 'run' || v === 'running') return 'Робота';
  if (v === 'standby' || v === 'idle') return 'Очікування';
  if (v === 'fault') return 'Аварія';
  return rs || '—';
};
const fmtWhen = (l: { readAtUtc: string | null; readAtLocal: string }) =>
  new Date(l.readAtUtc || l.readAtLocal).toLocaleString('uk-UA', { timeZone: l.readAtUtc ? 'Europe/Kyiv' : 'UTC' });

const short = (n: string) => n.replace(/^СЕС\s+/, '').trim();
const num = (n: number | null | undefined, d = 0, suf = '') =>
  n == null ? '—' : `${Number(n).toLocaleString('uk-UA', { minimumFractionDigits: d, maximumFractionDigits: d })}${suf ? ' ' + suf : ''}`;

function flowOf(s: BessSystem) {
  const pf = (s.live?.powerFlow || '').toLowerCase();
  const out = s.live?.outputKw ?? 0;
  if (pf.includes('charg') || out < -0.3) return { label: 'Заряджання', color: '#1F5B8F', icon: 'ri-battery-charge-line' };
  if (pf.includes('dischar') || out > 0.3) return { label: 'Розряджання', color: '#E0A030', icon: 'ri-flashlight-line' };
  return { label: 'Очікування', color: '#6B6B70', icon: 'ri-battery-line' };
}

export default function BessPage() {
  const [systems, setSystems] = useState<BessSystem[] | null>(null);
  const [sel, setSel] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    authFetch(`${API_BASE_URL}/owner/bess-fleet`)
      .then((r) => (r.ok ? r.json() : Promise.reject(`HTTP ${r.status}`)))
      .then((d) => {
        setSystems(d.systems || []);
        const firstActive = (d.systems || []).find((x: BessSystem) => x.status === 'active') || (d.systems || [])[0];
        if (firstActive) setSel(firstActive.assetId);
      })
      .catch((e) => setErr(String(e)));
  }, []);

  const current = useMemo(() => systems?.find((s) => s.assetId === sel) || null, [systems, sel]);

  const statusBadge = (s: BessSystem) => {
    if (s.status === 'planned') return { t: 'Запланована', c: '#7FB2E6' };
    if (s.online) return { t: 'Онлайн', c: '#22C55E' };
    return { t: 'Офлайн', c: 'var(--accent)' };
  };

  const Stat = ({ label, value, color }: { label: string; value: string; color?: string }) => (
    <div className="rounded-lg px-3 py-2" style={{ background: innerBg, border: `1px solid ${cardBorder}` }}>
      <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: textMuted }}>{label}</div>
      <div className="text-base font-bold tabular-nums" style={{ color: color || textMain }}>{value}</div>
    </div>
  );

  return (
    <DashboardLayout breadcrumb={['BESS']}>
      <div className="min-h-screen -m-4" style={{ background: '#111113' }}>
        <div className="px-4 md:px-6 pt-6 pb-10" style={{ color: textMain }}>
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Inter, sans-serif' }}>BESS — системи зберігання</h1>
            <p className="text-sm mt-1" style={{ color: textMuted }}>Перелік СЕС із накопичувачами. Оберіть станцію зі списку, щоб переглянути стан і технічні параметри BESS.</p>
          </div>

          {err && <div className="rounded-xl p-4 mb-4" style={{ background: cardBg, border: `1px solid ${cardBorder}`, color: 'var(--accent)' }}>Помилка: {err}</div>}

          <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-4">
            {/* LIST */}
            <div className="rounded-xl p-3 flex flex-col h-full" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
              <div className="text-[10px] font-bold uppercase tracking-widest mb-2 px-1" style={{ color: textMuted }}>Станції з BESS</div>
              {!systems && <div className="text-sm px-1 py-3" style={{ color: textMuted }}>Завантаження…</div>}
              {systems && systems.length === 0 && <div className="text-sm px-1 py-3" style={{ color: textMuted }}>BESS не налаштовано для вашого портфелю.</div>}
              <div className="flex flex-col gap-2">
                {(systems || []).map((s) => {
                  const b = statusBadge(s);
                  const f = flowOf(s);
                  const active = s.assetId === sel;
                  return (
                    <button
                      key={s.id}
                      onClick={() => setSel(s.assetId)}
                      className="text-left rounded-lg p-3 cursor-pointer transition-all w-full"
                      style={{ background: active ? hoverBg : 'transparent', border: `1px solid ${active ? '#1F5B8F' : cardBorder}` }}
                    >
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="text-sm font-semibold truncate" style={{ color: textMain }}>{short(s.name)}</span>
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap" style={{ background: `${b.c}20`, color: b.c }}>{b.t}</span>
                      </div>
                      {s.status === 'active' && s.live ? (
                        <div className="flex items-center justify-between">
                          <span className="text-lg font-bold tabular-nums" style={{ color: textMain }}>{num(s.live.socPct, 0, '%')}<span className="text-[10px] font-normal" style={{ color: textMuted }}> SoC</span></span>
                          <span className="text-[11px] font-medium flex items-center gap-1" style={{ color: f.color }}><i className={f.icon} /> {f.label}</span>
                        </div>
                      ) : (
                        <div className="text-[11px]" style={{ color: textDim }}>{s.status === 'planned' ? 'після запуску · телеметрії ще немає' : 'немає телеметрії'}</div>
                      )}
                      <div className="text-[10px] mt-1" style={{ color: textDim }}>
                        {s.nameplate.manufacturer || '—'} · {s.nameplate.powerKw ? `${num(s.nameplate.powerKw)} кВт` : '— кВт'}{s.nameplate.energyKwh ? ` / ${num(s.nameplate.energyKwh)} кВт·год` : ''}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* DETAIL */}
            <div className="flex flex-col gap-4">
              {!current && <div className="rounded-xl p-6 text-sm" style={{ background: cardBg, border: `1px solid ${cardBorder}`, color: textMuted }}>Оберіть станцію зі списку.</div>}
              {current && (
                <>
                  {/* Nameplate */}
                  <div className="rounded-xl p-4" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
                    <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                      <div>
                        <div className="text-lg font-bold" style={{ color: textMain }}>{short(current.name)}</div>
                        <div className="text-[11px]" style={{ color: textMuted }}>{current.region} · СЕС {num(current.installedMw, 0)} МВт</div>
                      </div>
                      <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full" style={{ background: `${statusBadge(current).c}20`, color: statusBadge(current).c }}>{statusBadge(current).t}</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <Stat label="Потужність (PCS)" value={num(current.nameplate.powerKw, 0, 'кВт')} />
                      <Stat label="Ємність" value={num(current.nameplate.energyKwh, 0, 'кВт·год')} />
                      <Stat label="Виробник" value={current.nameplate.manufacturer || '—'} />
                      <Stat label="Тип батареї" value={current.nameplate.chemistry || '—'} />
                    </div>
                    {current.nameplate.notes && <div className="text-[11px] mt-3" style={{ color: textDim }}>ℹ️ {current.nameplate.notes}</div>}
                  </div>

                  {current.status === 'planned' || !current.live ? (
                    <div className="rounded-xl p-6 text-sm text-center" style={{ background: cardBg, border: `1px solid ${cardBorder}`, color: textMuted }}>
                      {current.status === 'planned' ? '🔋 BESS заплановано — телеметрія з’явиться після запуску станції та підключення Modbus.' : 'Немає свіжої телеметрії BESS (можливо, офлайн).'}
                    </div>
                  ) : (
                    <>
                      {/* Live state */}
                      <div className="rounded-xl p-4" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="text-sm font-semibold" style={{ color: textMain }}>Поточний стан</div>
                          <div className="text-[10px]" style={{ color: textDim }}>оновлено {fmtWhen(current.live)}</div>
                        </div>
                        {statusUk(current.live.runningStatus) === 'Аварія' && (
                          <div className="rounded-lg px-3 py-2 mb-3 text-[11px] leading-relaxed" style={{ background: 'var(--surface-2)', border: '1px solid color-mix(in srgb, var(--err) 35%, transparent)', color: 'var(--text-muted)' }}>
                            <span style={{ color: 'var(--accent)', fontWeight: 700 }}>⚠️ PCS у стані «Аварія»</span> — інвертор BESS зупинено, заряд/розряд не виконується, SoC зафіксовано.{current.live.faults.length ? ` Коди аварій: ${current.live.faults.join(', ')}.` : ''} Дії: звірити коди з картою аварій Kehua, перевірити на об’єкті / через SCADA; за потреби — скинути аварію або викликати сервіс.
                          </div>
                        )}
                        <div className="mb-3">
                          <div className="flex items-center justify-between text-[11px] mb-1" style={{ color: textMuted }}>
                            <span>Рівень заряду (SoC)</span>
                            <span className="font-bold tabular-nums" style={{ color: textMain }}>{num(current.live.socPct, 0, '%')}</span>
                          </div>
                          <div className="w-full h-2.5 rounded-full overflow-hidden" style={{ background: innerBg }}>
                            <div className="h-full rounded-full" style={{ width: `${current.live.socPct ?? 0}%`, background: (current.live.socPct ?? 0) > 20 ? '#22C55E' : '#E0A030' }} />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          <Stat label="Режим" value={flowOf(current).label} color={flowOf(current).color} />
                          <Stat label="Потужність" value={num(current.live.outputKw, 1, 'кВт')} />
                          <Stat label="SoH (здоров’я)" value={num(current.live.sohPct, 0, '%')} />
                          <Stat label="Частота мережі" value={num(current.live.gridHz, 2, 'Гц')} />
                        </div>
                      </div>

                      {/* Energy + electrical + temp */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="rounded-xl p-4" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
                          <div className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: textMuted }}>Енергія</div>
                          <div className="space-y-1.5 text-[12px]">
                            <div className="flex justify-between"><span style={{ color: textMuted }}>Заряд (доба)</span><span className="font-semibold tabular-nums">{num(current.live.dailyChargeKwh, 1, 'кВт·год')}</span></div>
                            <div className="flex justify-between"><span style={{ color: textMuted }}>Розряд (доба)</span><span className="font-semibold tabular-nums">{num(current.live.dailyDischargeKwh, 1, 'кВт·год')}</span></div>
                            <div className="flex justify-between"><span style={{ color: textMuted }}>Заряд (всього)</span><span className="font-semibold tabular-nums">{num(current.live.totalChargeKwh, 0, 'кВт·год')}</span></div>
                            <div className="flex justify-between"><span style={{ color: textMuted }}>Розряд (всього)</span><span className="font-semibold tabular-nums">{num(current.live.totalDischargeKwh, 0, 'кВт·год')}</span></div>
                          </div>
                        </div>
                        <div className="rounded-xl p-4" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
                          <div className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: textMuted }}>Електричні</div>
                          <div className="space-y-1.5 text-[12px]">
                            <div className="flex justify-between"><span style={{ color: textMuted }}>DC напруга</span><span className="font-semibold tabular-nums">{num(current.live.dcVoltageV, 1, 'В')}</span></div>
                            <div className="flex justify-between"><span style={{ color: textMuted }}>DC струм</span><span className="font-semibold tabular-nums">{num(current.live.dcCurrentA, 1, 'А')}</span></div>
                            <div className="flex justify-between"><span style={{ color: textMuted }}>DC потужність</span><span className="font-semibold tabular-nums">{num(current.live.dcPowerKw, 1, 'кВт')}</span></div>
                            <div className="flex justify-between"><span style={{ color: textMuted }}>Доступна</span><span className="font-semibold tabular-nums">{num(current.live.availableKva, 1, 'кВА')}</span></div>
                          </div>
                        </div>
                        <div className="rounded-xl p-4" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
                          <div className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: textMuted }}>Температура / аварії</div>
                          <div className="space-y-1.5 text-[12px]">
                            <div className="flex justify-between"><span style={{ color: textMuted }}>Внутрішня</span><span className="font-semibold tabular-nums">{num(current.live.innerTempC, 0, '°C')}</span></div>
                            <div className="flex justify-between"><span style={{ color: textMuted }}>Радіатор</span><span className="font-semibold tabular-nums">{num(current.live.radiatorTempC, 0, '°C')}</span></div>
                            <div className="flex justify-between"><span style={{ color: textMuted }}>Статус</span><span className="font-semibold" style={{ color: statusUk(current.live.runningStatus) === 'Аварія' ? 'var(--accent)' : textMain }}>{statusUk(current.live.runningStatus)}</span></div>
                            <div className="flex justify-between"><span style={{ color: textMuted }}>Аварії</span><span className="font-semibold" style={{ color: current.live.faults.length ? 'var(--accent)' : '#22C55E' }}>{current.live.faults.length ? current.live.faults.join(', ') : 'немає'}</span></div>
                          </div>
                        </div>
                      </div>

                      {/* SoC/power chart + status table */}
                      <BessSocPowerChart assetId={current.assetId} />
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
